export interface Account {
  id: string;
  code: string;
  name: string;
  nature: string;
  type: string;
  is_control_account: boolean;
  allow_manual_entry: boolean;
  is_active: boolean;
  group_name: string | null;
}

const ORDER = ['ASSET', 'LIABILITY', 'EQUITY', 'INCOME', 'EXPENSE'];
const TITLE: Record<string, string> = {
  ASSET: 'What the shop owns',
  LIABILITY: 'What the shop owes',
  EQUITY: 'Capital',
  INCOME: 'Income',
  EXPENSE: 'Expenses',
};

/**
 * Picking an account.
 *
 * A flat list of eighty codes is unusable: a person looking for "rent" should
 * not have to know it lives at 6120, and a cashier should never be able to post
 * by hand into a control account that a subledger maintains. So accounts are
 * grouped the way a balance sheet reads, and the ones that cannot be posted to
 * are left out when posting rather than offered and then refused.
 */
export function AccountSelect({
  accounts,
  value,
  onChange,
  postableOnly,
  placeholder = 'Choose an account…',
  width = 'w-full',
}: {
  accounts: Account[];
  value: string;
  onChange: (id: string) => void;
  postableOnly?: boolean;
  placeholder?: string;
  width?: string;
}) {
  const usable = accounts.filter((a) => a.is_active && (!postableOnly || a.allow_manual_entry));

  return (
    <select className={`field ${width}`} value={value} onChange={(e) => onChange(e.target.value)}>
      <option value="">{placeholder}</option>
      {ORDER.filter((nature) => usable.some((a) => a.nature === nature)).map((nature) => (
        <optgroup key={nature} label={TITLE[nature] ?? nature}>
          {usable
            .filter((a) => a.nature === nature)
            .map((a) => (
              <option key={a.id} value={a.id}>
                {a.code} — {a.name}
                {a.is_control_account ? ' (subledger)' : ''}
              </option>
            ))}
        </optgroup>
      ))}
    </select>
  );
}
